import { useMemo } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useIsRestoring,
} from "@tanstack/react-query";
import { useAuth } from "~/lib/auth";
import {
  EMPTY_GUEST_STARTER_STATE,
  GUEST_STARTER_QUERY_KEY,
  type GuestStarterLog,
  type GuestStarterState,
} from "~/lib/guest-data";
import { backendConfigured } from "~/lib/constants";
import { starterLogsQueryKey, starterQueryKey } from "~/lib/query-client";
import * as Crypto from "expo-crypto";
import { unwrap } from "@brad-tipper/app-frontend";
import { api } from "~/api/client";
import type {
  Starter as ApiStarter,
  StarterLog as ApiStarterLog,
} from "~/api/generated/types.gen";
import type { Starter, StarterLog } from "~/types/models";

function mapStarter(row: ApiStarter): Starter {
  return {
    id: row.id,
    name: row.name,
    userId: row.owner_id,
    lastFedAt: row.last_fed_at,
    feedingIntervalHours: row.feeding_interval_hours,
    notes: row.notes,
    createdAt: row.created_at,
  };
}

function mapLog(row: ApiStarterLog): StarterLog {
  return {
    id: row.id,
    starterId: row.starter_id,
    userId: row.owner_id,
    fedAt: row.fed_at,
    notes: row.notes,
  };
}

function guestStarterToStarter(
  guest: NonNullable<GuestStarterState["starter"]>,
): Starter {
  return {
    id: guest.id,
    name: guest.name,
    userId: "",
    lastFedAt: guest.lastFedAt,
    feedingIntervalHours: guest.feedingIntervalHours,
    notes: guest.notes,
    createdAt: guest.createdAt,
  };
}

function guestLogToLog(guest: GuestStarterLog): StarterLog {
  return {
    id: guest.id,
    starterId: guest.starterId,
    userId: "",
    fedAt: guest.fedAt,
    notes: guest.notes,
  };
}

const newId = () =>
  `guest-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export function useStarter() {
  const { user, hasPro } = useAuth();
  const queryClient = useQueryClient();
  // Free accounts (signed in or not) stay local-only; only Pro subscribers
  // get cross-device sync via the API.
  const useLocal = !backendConfigured || !user || !hasPro;
  // The persisted guest cache restores asynchronously; fetching before it finishes
  // would let the empty default overwrite saved data on reload.
  const isRestoring = useIsRestoring();

  const guestQuery = useQuery({
    queryKey: GUEST_STARTER_QUERY_KEY,
    queryFn: (): GuestStarterState => EMPTY_GUEST_STARTER_STATE,
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: useLocal && !isRestoring,
  });
  const guestState = guestQuery.data ?? EMPTY_GUEST_STARTER_STATE;

  const starterQuery = useQuery({
    queryKey: starterQueryKey(user?.id ?? ""),
    enabled: Boolean(user && !useLocal),
    queryFn: async () => {
      if (!user) return null;
      const list = await unwrap(api.listStarters());
      const latest = [...list.items].sort((a, b) =>
        a.created_at < b.created_at ? 1 : -1,
      )[0];
      return latest ? mapStarter(latest) : null;
    },
  });

  const starter = useLocal
    ? guestState.starter
      ? guestStarterToStarter(guestState.starter)
      : null
    : (starterQuery.data ?? null);
  const starterId = useLocal ? guestState.starter?.id : starterQuery.data?.id;

  const logsQuery = useQuery({
    queryKey: starterLogsQueryKey(starterId ?? ""),
    enabled: Boolean(!useLocal && starterId),
    queryFn: async () => {
      if (!starterId) return [];
      const list = await unwrap(api.listStarterLogs());
      return list.items
        .filter((row) => row.starter_id === starterId)
        .sort((a, b) => (a.fed_at < b.fed_at ? 1 : -1))
        .map(mapLog);
    },
  });

  const logs = useLocal
    ? guestState.logs.map(guestLogToLog)
    : (logsQuery.data ?? []);

  const writeGuest = (
    updater: (current: GuestStarterState) => GuestStarterState,
  ) => {
    queryClient.setQueryData<GuestStarterState>(
      GUEST_STARTER_QUERY_KEY,
      (current) => updater(current ?? EMPTY_GUEST_STARTER_STATE),
    );
  };

  const createStarterMutation = useMutation({
    mutationFn: async (payload: {
      name: string;
      feedingIntervalHours: number;
      notes: string;
    }) => {
      if (!user) throw new Error("Must be signed in");
      return mapStarter(
        await unwrap(
          api.putStarter({
            path: { id: Crypto.randomUUID() },
            body: {
              name: payload.name,
              feeding_interval_hours: payload.feedingIntervalHours,
              notes: payload.notes,
              last_fed_at: new Date().toISOString(),
            },
          }),
        ),
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: starterQueryKey(user?.id ?? ""),
      });
    },
  });

  const feedStarterMutation = useMutation({
    mutationFn: async () => {
      if (!user || !starterId) throw new Error("No starter to feed");
      const fedAt = new Date().toISOString();
      await unwrap(
        api.putStarterLog({
          path: { id: Crypto.randomUUID() },
          body: { starter_id: starterId, fed_at: fedAt },
        }),
      );
      return mapStarter(
        await unwrap(
          api.patchStarter({
            path: { id: starterId },
            body: { last_fed_at: fedAt },
          }),
        ),
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: starterQueryKey(user?.id ?? ""),
        }),
        queryClient.invalidateQueries({
          queryKey: starterLogsQueryKey(starterId ?? ""),
        }),
      ]);
    },
  });

  const deleteStarterMutation = useMutation({
    mutationFn: async () => {
      if (!starterId) throw new Error("No starter to delete");
      await unwrap(
        api.deleteStarter({
          path: { id: starterId },
          query: { permanent: true },
        }),
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: starterQueryKey(user?.id ?? ""),
        }),
        queryClient.invalidateQueries({ queryKey: ["starter-logs"] }),
      ]);
    },
  });

  const hoursUntilNextFeed = useMemo(
    () => () => {
      if (!starter) return null;
      const lastFed = new Date(starter.lastFedAt);
      if (
        Number.isNaN(lastFed.getTime()) ||
        !Number.isFinite(starter.feedingIntervalHours)
      ) {
        return null;
      }
      const nextFeed = new Date(
        lastFed.getTime() + starter.feedingIntervalHours * 60 * 60 * 1000,
      );
      const hoursRemaining =
        (nextFeed.getTime() - Date.now()) / (1000 * 60 * 60);
      return Number.isFinite(hoursRemaining) ? hoursRemaining : null;
    },
    [starter],
  );

  return {
    starter,
    logs,
    loading: useLocal
      ? guestQuery.isPending
      : starterQuery.isPending || Boolean(starterId && logsQuery.isPending),
    error: useLocal
      ? null
      : (starterQuery.error?.message ?? logsQuery.error?.message ?? null),
    createStarter: async (
      name: string,
      feedingIntervalHours: number,
      notes: string,
    ) => {
      if (useLocal) {
        writeGuest((current) => ({
          ...current,
          starter: {
            id: newId(),
            name,
            lastFedAt: new Date().toISOString(),
            feedingIntervalHours,
            notes,
            createdAt: new Date().toISOString(),
          },
        }));
        return;
      }
      await createStarterMutation.mutateAsync({
        name,
        feedingIntervalHours,
        notes,
      });
    },
    feedStarter: async () => {
      if (useLocal) {
        if (!guestState.starter) return;
        const fedAt = new Date().toISOString();
        writeGuest((current) => ({
          starter: current.starter
            ? { ...current.starter, lastFedAt: fedAt }
            : null,
          logs: [
            {
              id: newId(),
              starterId: current.starter?.id ?? "",
              fedAt,
              notes: "",
            },
            ...current.logs,
          ],
        }));
        return;
      }
      await feedStarterMutation.mutateAsync();
    },
    deleteStarter: async () => {
      if (useLocal) {
        writeGuest(() => EMPTY_GUEST_STARTER_STATE);
        return;
      }
      await deleteStarterMutation.mutateAsync();
    },
    hoursUntilNextFeed,
    refetch: starterQuery.refetch,
  };
}
